import { BehaviorSubject, distinctUntilChanged, filter, firstValueFrom, Observable, of, merge, Subscription, take } from 'rxjs';
import { retryCancel } from '@weavix/utils/src/retry-cancel';
import { SyncHttp, SyncMqtt, SyncStorage } from './sync.model';
import { SyncModule } from './sync.module';
import { PriorityQueue } from './priority-queue';
import { PermissionAction } from '@weavix/models/src/permission/permissions.model';
import { i18nLocale } from '@weavix/models/src/translate/translate';
import { TelemetryEvent, TelemetryEventPayloads } from '@weavix/telemetry';
import { AccountDisclaimers } from '@weavix/models/src/account/account';

type AccountEvent = { id: string, removed?: boolean };
export interface UserAccount {
    id: string;
    accounts: AccountInfo[];
    removedAccountIds?: string[]; // list of account IDs the user has been removed from
    company: { id: string };
    bleIds?: number[];
    phone?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    locale?: i18nLocale;
    dialect?: string;
    enabledActions?: PermissionAction[];

    /**
     * anything on or before these dates have been deleted by data retention
     */
    archived?: {
        /** mass alerts */
        alerts?: Date;
        /** events and badge updates */
        events?: Date;
    };
}

export interface AccountInfo {
    id: string;
    companyId?: string;
    companyIds?: string[];
    enabledActions?: string[];
    allCompanies?: boolean;
    image: string;
    name: string;
    features: Array<{ key: string, value: number }>;
    master?: boolean;
    color?: string;
    disclaimers?: AccountDisclaimers;

    /**
     * anything on or before these dates have been deleted by data retention
     */
    archived?: {
        /** mass alerts */
        alerts?: Date;
        /** events and badge updates */
        events?: Date;
    };
}

export type SyncDestroyer = undefined | (() => void);
export type SyncTrigger = (user: UserAccount, cache?: boolean) => SyncDestroyer | Promise<SyncDestroyer>;

interface SyncMetrics {
    records: number;
    bytes: number;
    partitionsCount: number;
}

interface SyncStoreLoading {
    [storeName: string]: BehaviorSubject<number> | undefined;
}

type UserSyncProperties = { id: '0', version: string, userId?: string, waltId?: string };

export interface SyncAuthentication {
    user?: UserAccount;
    walt?: WaltAccount;
}

export interface WaltAccount {
    id: string;
    accountId?: string;
}

export class SyncService {
    static triggers = new Map<string, SyncTrigger>();
    static destroyers = new Map<string, SyncDestroyer | Promise<SyncDestroyer>>();
    static instance?: SyncService;

    static user$ = new BehaviorSubject<UserAccount | null>(null);
    static authentication$ = new BehaviorSubject<SyncAuthentication | null>(null);
    // update this to force re-sync on client update
    // native sync ignores this; bump SyncModuleDefinition.schemaVersion for the same model change
    static version = 'v4';

    // Triggers are triggered when the user is set or changed, destroyed on logout or user is changed
    static addTrigger(name: string, trigger: SyncTrigger) {
        console.log(`Adding sync trigger: ${name}, existed: ${SyncService.triggers.has(name)}`);
        this.triggers.set(name, trigger);
        const user = SyncService.user$?.value;
        SyncService.fireTrigger(name, trigger, user, true);
    }

    private userId?: string;

    private readonly priorityQueue = new PriorityQueue();

    private readonly syncStoreLoading: SyncStoreLoading = {};
    readonly priorityLoading$ = new BehaviorSubject(0);
    readonly lazyLoading$ = new BehaviorSubject(0);

    private accountSubscription?: Subscription;
    private waltSubscription?: Subscription;
    private metrics: SyncMetrics = { records: 0, bytes: 0, partitionsCount: 0 };

    constructor(
        private readonly http: SyncHttp,
        private readonly mqtt: SyncMqtt,
        private readonly syncStorage: SyncStorage<UserAccount | WaltAccount>,
        private readonly waltId?: string | undefined,
    ) {}

    /**
     * Does a single initialization with a known user.
     *
     * This is intended for if you externally manage the current user.
     * Otherwise, you should call {@link loggedIn} to automatically re-initialize upon changes.
     */
    async initialize(userAccount: UserAccount) {
        this.userId = userAccount.id;

        await this.syncStorage.add(userAccount);
        await this.setSyncProperties(userAccount.id);

        SyncService.trigger(userAccount);
        void this.emitAuth();
    }

    sendTelemetry<E extends TelemetryEvent>(name: E, data: TelemetryEventPayloads[E]) {
        this.mqtt.sendTelemetry(name, data);
    }

    getCurrentNetworks() {
        return this.mqtt.getCurrentNetworks?.() ?? {};
    }

    async emitAuth() {
        const properties = await this.getSyncProperties();
        const user = await this.syncStorage.get(properties?.userId ?? '-1') as UserAccount | null;
        const walt = this.waltId ? await this.syncStorage.get(this.waltId) as WaltAccount | null : undefined;
        SyncService.authentication$.next({ user: user ?? undefined, walt: walt ?? undefined });
    }

    /**
     * Begins automatic re-initializing for the current user.
     *
     * If you just want to do a single initialization, use {@link initialize} instead.
     */
    async loggedIn() {
        this.metrics = { records: 0, bytes: 0, partitionsCount: 0 };

        const properties = await this.getSyncProperties();
        this.userId = properties?.version === SyncService.version ? properties.userId ?? '-1' : '-1';
        const user = this.userId === '-1' ? null : (await this.syncStorage.get(this.userId)) as UserAccount;
        if (user) {
            SyncService.trigger(user, true);
            void this.emitAuth();
        }
        const wait = this.subscribeUserAccounts();
        if (!user) {
            await wait;
            return false;
        }
        return true;
    }

    private async subscribeUserAccounts() {
        const refreshAccount = async () => {
            const user = await this.get<UserAccount>('/core/me/user');
            this.userId = user.id;
            await this.syncStorage.add(user);
            await this.setSyncProperties(user.id);
            SyncService.trigger(user);
            void this.emitAuth();
        };
        const refreshSubscription = async () => {
            const subs = await Promise.all([
                this.subscribe<AccountEvent>(`user/${this.userId}/account-added`),
                this.subscribe<AccountEvent>(`user/${this.userId}/account-removed`),
            ]);
            this.accountSubscription?.unsubscribe();
            this.accountSubscription = merge(...subs).subscribe({
                next: () => {
                    void refreshAccount();
                },
                error: () => {
                    void this.subscribeUserAccounts();
                },
            });
        };
        const userPromise = refreshAccount();
        if (this.userId === '-1') await userPromise;
        void refreshSubscription();
        await userPromise;
    }

    async subscribeWaltAccount() {
        this.waltSubscription?.unsubscribe();
        const refreshWalt = async () => {
            const walt = await this.get<WaltAccount>(`/track/walt/${this.waltId}/assignment`, undefined, () => !this.waltId);
            await this.syncStorage.add(walt);
            void this.emitAuth();
        };
        const refreshWaltSubscription = async () => {
            this.waltSubscription = (await this.subscribe<WaltAccount>(`walt/${this.waltId}/updated`, () => !this.waltId)).pipe(
                distinctUntilChanged((prev, curr) => prev?.payload?.accountId === curr?.payload?.accountId),
            ).subscribe({
                next: () => {
                    void refreshWalt();
                },
                error: () => {
                    void this.subscribeWaltAccount();
                },
            });
        };
        const waltPromise = refreshWalt();
        void refreshWaltSubscription();
        await waltPromise;
    }

    private async getSyncProperties() {
        return await this.syncStorage.get('0') as unknown as UserSyncProperties | null | undefined;
    }

    private async setSyncProperties(userId?: string) {
        await this.syncStorage.add({ id: '0', userId, waltId: this.waltId, version: SyncService.version } satisfies UserSyncProperties as unknown as UserAccount);
    }

    reportPartitionMetric(records: number, bytes: number) {
        this.metrics.records += records;
        this.metrics.bytes += bytes;
        this.metrics.partitionsCount += 1;
    }

    async waitForModules(from?: string) {
        if (from) console.log(`${from} is waiting for sync modules`);
        await firstValueFrom(
            this.priorityLoading$.pipe(
                filter(val => val <= 0),
                take(1),
            ),
        );
        await firstValueFrom(
            this.lazyLoading$.pipe(
                filter(val => val <= 0),
                take(1),
            ),
        );
        return this.metrics;
    }

    async loggedOut(reset = false) {
        this.accountSubscription?.unsubscribe();
        this.accountSubscription = undefined;
        if (reset) {
            const userId = (await this.getSyncProperties())?.userId;
            if (userId) await this.syncStorage.remove(userId);
            await this.setSyncProperties(undefined);
        }
        this.userId = undefined;
        SyncService.trigger(null);
        void this.emitAuth();

        await Promise.all(SyncModule.modules.map(async mod => {
            await mod.close(reset);
        }));
    }

    async flush() {
        await Promise.all(SyncModule.modules.map(async mod => {
            await mod.flush();
        }));
    }

    static trigger(user: UserAccount | null, cache = false) {
        [...SyncService.triggers.entries()].forEach(([name, trigger]) => this.fireTrigger(name, trigger, user, cache));
        SyncService.user$.next(user);
    }

    private static fireTrigger(name: string, trigger: SyncTrigger, user: UserAccount | null, cache = false) {
        let destroyer = SyncService.destroyers.get(name);

        SyncService.destroyers.set(name, (async () => {
            try { (await destroyer)?.(); } catch (e) { console.error(`SyncService.destroyer ${name}`, e); }
            destroyer = undefined;

            if (!user) return undefined;
            console.log(`Firing sync trigger: ${name} for user ${user.id}, cache: ${cache}`);
            return trigger(user, cache);
        })());
    }

    /**
     * Returns a promise that resolves when subscribed to the MQTT topic.
     * The resolved value is an observable that emits the messages received on the topic.
     *
     * NOTE: If cancelled, the returned promise will never resolve!
     */
    subscribe<T>(topic: string, cancelled: () => boolean = () => !this.userId, lazy = false, simulcast = false) {
        const fn = () => retryCancel(async () => {
            const result = simulcast ? this.mqtt.subscribeMultiple<T>(topic) : this.mqtt.subscribe<T>(topic);
            await result.subscribed;
            return result;
        }, cancelled);
        return lazy ? this.priorityQueue.queue(fn) : this.priorityQueue.run(fn);
    }

    /**
     * Makes an HTTP GET with retries and priority queuing (`lazy`).
     *
     * NOTE: If cancelled, the returned promise will never resolve!
     */
    async get<T>(path: string, params?: unknown, cancelled: () => boolean = () => !this.userId, lazy = false) {
        console.log(`GET ${path} ${SyncService.user$.value?.id}`);
        const fn = () => retryCancel(() => this.http.get<T>(path, params), cancelled);
        return lazy ? this.priorityQueue.queue(fn) : this.priorityQueue.run(fn);
    }

    /**
     * Makes an HTTP POST with retries and priority queuing (`lazy`).
     *
     * NOTE: If cancelled, the returned promise will never resolve!
     */
    async post<T>(path: string, body?: unknown, params?: unknown, cancelled: () => boolean = () => !this.userId, lazy = false) {
        console.log(`POST ${path} ${SyncService.user$.value?.id}`);
        const fn = () => retryCancel(() => this.http.post<T>(path, body, params), cancelled);
        return lazy ? this.priorityQueue.queue(fn) : this.priorityQueue.run(fn);
    }

    changeLoadingCount(change: number, storeName: string): void {
        const loading$ = this.syncStoreLoading[storeName];
        if (!loading$) {
            this.syncStoreLoading[storeName] = new BehaviorSubject<number>(Math.max(0, change));
        } else {
            loading$.next(loading$.value + change);
        }
    }

    getLoadingCount$(storeName: string): Observable<number> {
        return this.syncStoreLoading[storeName]?.asObservable() ?? of();
    }
}
